(() => {
  const MEDIA_COLOR_PATTERN = /variant-color:([a-z0-9-]+)/;
  const USER_CHANGE_SYNC_DELAYS = [120];
  const POST_DAWN_SYNC_DELAYS = [0, 120];
  const INITIAL_SYNC_DELAYS = [0, 120, 400];
  const FULL_WIDTH_CLASS = 'product__media-item--full';
  const STALE_LAYOUT_CLASSES = [
    FULL_WIDTH_CLASS,
    'joye-media-item--full',
    'joye-media-item--half',
    'full-width',
    'half-width',
  ];
  const warnedMessages = new Set();
  let syncTimers = [];
  let lastActivatedMediaId = '';
  let lastFilterSignature = '';
  const boundVariantSelects = new WeakSet();

  function slugifySelectedColor(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[&/]/g, '')
      .replace(/\s+/g, ' ')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function normalizeLayoutValue(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function warnOnce(key, ...args) {
    if (warnedMessages.has(key)) return;
    warnedMessages.add(key);
    console.warn(...args);
  }

  function getVariantSelects() {
    return document.querySelector('variant-selects');
  }

  function getSelectedColor() {
    const variantSelects = getVariantSelects();
    if (!variantSelects) return '';

    const checkedInput = variantSelects.querySelector('input[type="radio"]:checked');
    if (checkedInput && checkedInput.value) {
      return checkedInput.value;
    }

    const select = variantSelects.querySelector('select');
    if (select && select.value) {
      return select.value;
    }

    return '';
  }

  function getSelectedOptionElement() {
    const variantSelects = getVariantSelects();
    if (!variantSelects) return null;

    return variantSelects.querySelector('input[type="radio"]:checked, select option:checked');
  }

  function getVariantFromSelectedOption() {
    const selectedOption = getSelectedOptionElement();
    if (!selectedOption || !selectedOption.dataset.variantId) return null;

    return {
      id: selectedOption.dataset.variantId,
      available: selectedOption.dataset.variantAvailable === 'true',
    };
  }

  function getVariantIdFromUrl() {
    try {
      return new URLSearchParams(window.location.search).get('variant') || '';
    } catch (error) {
      return '';
    }
  }

  function getSelectedVariantFromJson() {
    const variantSelects = getVariantSelects();
    const selectedVariantJson = variantSelects?.querySelector('[data-selected-variant]')?.textContent;
    if (!selectedVariantJson) return null;

    try {
      const variant = JSON.parse(selectedVariantJson);
      return variant && variant.id ? variant : null;
    } catch (error) {
      warnOnce('selected-variant-json', '[JoyeGlasses media filter] Could not parse selected variant JSON.', error);
      return null;
    }
  }

  function getCurrentVariant() {
    return getVariantFromSelectedOption() || getSelectedVariantFromJson();
  }

  function getMediaSlugFromItem(item) {
    const image = item.querySelector('img');
    const alt = image ? image.getAttribute('alt') || '' : '';
    const match = alt.toLowerCase().match(MEDIA_COLOR_PATTERN);
    return match ? match[1] : '';
  }

  function getGalleryItems() {
    return Array.from(document.querySelectorAll('ul[id^="Slider-Gallery-"] .product__media-item'));
  }

  function getMediaLayout(item) {
    const explicitLayout =
      item.dataset?.mediaLayout ||
      item.dataset?.mediaView ||
      item.getAttribute?.('data-media-layout') ||
      item.getAttribute?.('data-media-view') ||
      '';
    const normalizedLayout = normalizeLayoutValue(explicitLayout);

    if (
      normalizedLayout === 'full' ||
      normalizedLayout === 'full-width' ||
      /^(?:01-)?front(?:-view)?$/.test(normalizedLayout) ||
      normalizedLayout === '04-model-fit'
    ) {
      return 'full';
    }
    if (
      normalizedLayout === 'half' ||
      normalizedLayout === 'half-width' ||
      /^02-45(?:-degree)?-view$/.test(normalizedLayout) ||
      normalizedLayout === '03-side-view'
    ) {
      return 'half';
    }

    const image = item.querySelector('img');
    const alt = image ? image.getAttribute('alt') || '' : '';

    if (!getMediaSlugFromItem(item)) return 'full';
    if (/\b(?:01[\s_-]*)?front(?:[\s_-]*view)?\b/i.test(alt)) return 'full';
    if (/\b04[\s_-]*model[\s_-]*fit\b/i.test(alt)) return 'full';
    if (/\b02[\s_-]*45(?:°|[\s_-]*degree)?[\s_-]*view\b/i.test(alt)) return 'half';
    if (/\b03[\s_-]*side[\s_-]*view\b/i.test(alt)) return 'half';

    return 'half';
  }

  function isMediaItemVisible(item) {
    if (item.hidden || item.style.display === 'none') return false;
    if (typeof window.getComputedStyle !== 'function') return true;
    return window.getComputedStyle(item).display !== 'none';
  }

  function recalculateVisibleMediaLayout(galleryItems = getGalleryItems()) {
    galleryItems
      .filter(isMediaItemVisible)
      .forEach((item) => {
        item.classList.remove(...STALE_LAYOUT_CLASSES);
        item.style.removeProperty('grid-column');
        item.style.removeProperty('grid-row');
        item.style.removeProperty('grid-area');

        if (getMediaLayout(item) === 'full') {
          item.classList.add(FULL_WIDTH_CLASS);
        }
      });
  }

  function getThumbnailItems() {
    return Array.from(document.querySelectorAll('ul[id^="Slider-Thumbnails-"] .thumbnail-list__item'));
  }

  function setItemVisible(item, visible) {
    const displayValue = visible ? '' : 'none';
    if (item.hidden !== !visible) item.hidden = !visible;
    if (item.style.display !== displayValue) item.style.display = displayValue;
  }

  function getItemColorMap(items) {
    return items.map((item) => ({
      item,
      color: getMediaSlugFromItem(item),
    }));
  }

  function countMatches(itemMap, selectedColor) {
    return itemMap.filter(({ color }) => color === selectedColor).length;
  }

  function getMediaIds(itemMap, selectedColor) {
    return itemMap
      .filter(({ color }) => color === selectedColor)
      .map(({ item }) => item.dataset.mediaId || item.dataset.target || item.id || '')
      .join(',');
  }

  function getFilterSignature(selectedColor, galleryMap, thumbnailMap) {
    return [
      selectedColor,
      countMatches(galleryMap, selectedColor),
      countMatches(thumbnailMap, selectedColor),
      galleryMap.length,
      thumbnailMap.length,
      getMediaIds(galleryMap, selectedColor),
      getMediaIds(thumbnailMap, selectedColor),
    ].join('|');
  }

  function isActiveMediaVisible() {
    const activeMedia = document.querySelector('ul[id^="Slider-Gallery-"] .product__media-item.is-active');
    if (!activeMedia) return false;

    if (activeMedia.hidden || activeMedia.style.display === 'none') return false;
    if (typeof window.getComputedStyle !== 'function') return true;
    return window.getComputedStyle(activeMedia).display !== 'none';
  }

  function isFrontMediaForColor(item, selectedColor) {
    if (getMediaSlugFromItem(item) !== selectedColor) return false;
    const image = item.querySelector('img');
    const alt = image ? image.getAttribute('alt') || '' : '';
    return /\b01[\s_-]*front(?:[\s_-]*view)?\b/i.test(alt);
  }

  function getFirstVisibleGalleryItem(selectedColor) {
    const visibleItems = getGalleryItems().filter((item) => !item.hidden && item.style.display !== 'none');
    return visibleItems.find((item) => isFrontMediaForColor(item, selectedColor)) || visibleItems[0];
  }

  function activateFirstVisibleThumbnail(selectedColor) {
    if (isActiveMediaVisible()) return;

    const firstVisibleGalleryItem = getFirstVisibleGalleryItem(selectedColor);
    if (!firstVisibleGalleryItem) return;

    const mediaId = firstVisibleGalleryItem.getAttribute('data-media-id');
    if (!mediaId) return;

    if (mediaId === lastActivatedMediaId) return;
    lastActivatedMediaId = mediaId;

    const matchingThumbnail = getThumbnailItems().find((item) => item.dataset.target === mediaId);
    if (!matchingThumbnail) return;

    const button = matchingThumbnail.querySelector('button');
    if (!button) return;

    window.requestAnimationFrame(() => {
      button.click();
    });
  }

  function syncSliderControls() {
    document.querySelectorAll('media-gallery').forEach((gallery) => {
      const viewer = gallery.querySelector('[id^="GalleryViewer-"]');
      const galleryItems = Array.from(
        gallery.querySelectorAll('ul[id^="Slider-Gallery-"] .product__media-item')
      );
      const visibleItems = galleryItems.filter((item) => !item.hidden && item.style.display !== 'none');
      const activeIndex = visibleItems.findIndex((item) => item.classList.contains('is-active'));
      const current = viewer?.querySelector('.slider-counter--current');
      const total = viewer?.querySelector('.slider-counter--total');

      if (current) current.textContent = String(activeIndex >= 0 ? activeIndex + 1 : visibleItems.length ? 1 : 0);
      if (total) total.textContent = String(visibleItems.length);

      viewer?.querySelectorAll('.slider-button').forEach((button) => {
        button.toggleAttribute('disabled', visibleItems.length <= 1);
      });

      viewer?.querySelectorAll('.slider-counter__link').forEach((dot, index) => {
        const visible = Boolean(galleryItems[index] && !galleryItems[index].hidden);
        setItemVisible(dot, visible);
      });

      viewer?.resetPages?.();
      gallery.querySelector('[id^="GalleryThumbnails-"]')?.resetPages?.();
    });
  }

  function syncVariantInput() {
    const variant = getCurrentVariant();
    if (!variant || !variant.id) {
      warnOnce('missing-variant', '[JoyeGlasses media filter] Current variant data was not found; skipped variant input sync.');
      return null;
    }

    const productInfo = document.querySelector('product-info');
    const formSelector = productInfo
      ? `#product-form-${productInfo.dataset.section}, #product-form-installment-${productInfo.dataset.section}`
      : 'form[action*="/cart/add"]';
    const variantInputs = document.querySelectorAll(`${formSelector} input[name="id"]`);

    variantInputs.forEach((input) => {
      if (variant.available) input.removeAttribute('disabled');
      else input.setAttribute('disabled', 'disabled');

      if (String(input.value) === String(variant.id)) return;
      input.value = variant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    return variant;
  }

  function syncSubmitButton(variant) {
    if (!variant || !variant.id || typeof variant.available !== 'boolean') return;

    const productForm = document.querySelector('product-info product-form');
    if (!productForm) return;

    const submitButton =
      productForm.querySelector('[id^="ProductSubmitButton-"]') ||
      productForm.querySelector('button[type="submit"], input[type="submit"]');
    if (!submitButton) return;

    const submitText = submitButton.querySelector('span') || submitButton;

    if (variant.available) {
      submitButton.removeAttribute('disabled');
      submitButton.removeAttribute('aria-disabled');
      if (submitText) submitText.textContent = window.variantStrings?.addToCart || 'Add to cart';
      return;
    }

    submitButton.setAttribute('disabled', 'disabled');
    submitButton.setAttribute('aria-disabled', 'true');
    if (submitText) submitText.textContent = window.variantStrings?.soldOut || 'Sold out';
  }

  function filterMediaBySelectedColor(options = {}) {
    const allowThumbnailActivation = options.allowThumbnailActivation === true;
    const selectedColor = slugifySelectedColor(getSelectedColor());
    if (!selectedColor) {
      recalculateVisibleMediaLayout();
      syncSliderControls();
      return;
    }

    const galleryItems = getGalleryItems();
    const thumbnailItems = getThumbnailItems();

    if (!galleryItems.length && !thumbnailItems.length) {
      recalculateVisibleMediaLayout(galleryItems);
      syncSliderControls();
      return;
    }

    const galleryMap = getItemColorMap(galleryItems);
    const thumbnailMap = getItemColorMap(thumbnailItems);
    const hasAnyColorTaggedMedia = galleryMap.some(({ color }) => color) || thumbnailMap.some(({ color }) => color);

    if (!hasAnyColorTaggedMedia) {
      galleryItems.forEach((item) => setItemVisible(item, true));
      thumbnailItems.forEach((item) => setItemVisible(item, true));
      recalculateVisibleMediaLayout(galleryItems);
      syncSliderControls();
      lastActivatedMediaId = '';
      if (allowThumbnailActivation) activateFirstVisibleThumbnail();
      lastFilterSignature = '';
      return;
    }

    const galleryMatchCount = countMatches(galleryMap, selectedColor);
    const thumbnailMatchCount = countMatches(thumbnailMap, selectedColor);
    const filterSignature = getFilterSignature(selectedColor, galleryMap, thumbnailMap);

    // Reapply visibility before cache reuse: rebuilt nodes can retain the same IDs.
    galleryMap.forEach(({ item, color }) => {
      setItemVisible(item, !color || color === selectedColor);
    });

    thumbnailMap.forEach(({ item, color }) => {
      setItemVisible(item, !color || color === selectedColor);
    });

    if (filterSignature === lastFilterSignature) {
      recalculateVisibleMediaLayout(galleryItems);
      syncSliderControls();
      if (allowThumbnailActivation) activateFirstVisibleThumbnail(selectedColor);
      return;
    }

    if (galleryMatchCount !== thumbnailMatchCount) {
      warnOnce(
        `media-count-${selectedColor}-${galleryMatchCount}-${thumbnailMatchCount}`,
        '[JoyeGlasses media filter] Media match count differs for color:',
        selectedColor,
        { gallery: galleryMatchCount, thumbnails: thumbnailMatchCount }
      );
    }



    recalculateVisibleMediaLayout(galleryItems);
    syncSliderControls();
    lastFilterSignature = filterSignature;
    if (allowThumbnailActivation) activateFirstVisibleThumbnail(selectedColor);
  }

  function syncJoyeVariantStateAndMedia(options = {}) {
    const variant = syncVariantInput();
    filterMediaBySelectedColor({
      allowThumbnailActivation: options.allowThumbnailActivation === true,
    });
    if (options.syncSubmitButton === true) syncSubmitButton(variant);
  }

  function getSyncDelays(reason) {
    if (
      reason === 'product-info-loaded' ||
      reason === 'variant-change-event' ||
      reason === 'variant-colon-change'
    ) {
      return POST_DAWN_SYNC_DELAYS;
    }

    if (
      reason === 'window-load' ||
      reason === 'url-variant' ||
      reason === 'initial' ||
      reason === 'section-load'
    ) {
      return INITIAL_SYNC_DELAYS;
    }

    return USER_CHANGE_SYNC_DELAYS;
  }

  function getSyncOptions(reason) {
    return {
      allowThumbnailActivation:
        reason === 'product-info-loaded' ||
        reason === 'variant-change-event' ||
        reason === 'variant-colon-change' ||
        reason === 'section-load' ||
        reason === 'window-load' ||
        reason === 'url-variant' ||
        reason === 'initial',
      syncSubmitButton: reason === 'product-info-loaded',
    };
  }

  function scheduleJoyeMediaSync(reason) {
    syncTimers.forEach((timer) => window.clearTimeout(timer));
    const syncOptions = getSyncOptions(reason);
    syncTimers = getSyncDelays(reason).map((delay) =>
      window.setTimeout(() => {
        if (reason === 'variant-change') lastActivatedMediaId = '';
        syncJoyeVariantStateAndMedia(syncOptions);
      }, delay)
    );
  }

  function isVariantControl(target) {
    return Boolean(target?.closest?.('variant-selects'));
  }

  function isSwatchControl(target) {
    return Boolean(target?.closest?.('variant-selects .product-form__input--swatch'));
  }

  function bindVariantSelects(variantSelects = getVariantSelects()) {
    if (!variantSelects || boundVariantSelects.has(variantSelects)) return;
    boundVariantSelects.add(variantSelects);

    variantSelects.addEventListener('change', () => {
      lastActivatedMediaId = '';
      scheduleJoyeMediaSync('variant-change');
    });

    variantSelects.addEventListener('click', (event) => {
      if (!isSwatchControl(event.target)) return;
      lastActivatedMediaId = '';
      scheduleJoyeMediaSync('swatch-click');
    });
  }

  function updateCardImage(image, source, sourceSet, alt) {
    if (!image || !source) return;
    image.src = source;
    if (sourceSet) image.srcset = sourceSet;
    else image.removeAttribute('srcset');
    if (alt) image.alt = alt;
  }

  function selectCardColor(button) {
    const card = button.closest('[data-joye-product-card]');
    if (!card || !button.dataset.frontSrc) return;

    const frontImage = card.querySelector('.joye-card-image--front');
    let hoverImage = card.querySelector('.joye-card-image--hover');
    updateCardImage(frontImage, button.dataset.frontSrc, button.dataset.frontSrcset, button.dataset.frontAlt);

    if (button.dataset.hoverSrc) {
      if (!hoverImage && frontImage) {
        hoverImage = frontImage.cloneNode(false);
        hoverImage.classList.remove('joye-card-image--front');
        hoverImage.classList.add('joye-card-image--hover');
        frontImage.parentElement.appendChild(hoverImage);
      }
      updateCardImage(hoverImage, button.dataset.hoverSrc, button.dataset.hoverSrcset, button.dataset.hoverAlt);
      hoverImage?.removeAttribute('hidden');
    } else if (hoverImage) {
      hoverImage.setAttribute('hidden', 'hidden');
    }

    card.querySelectorAll('[data-joye-color-swatch]').forEach((swatch) => {
      const isSelected = swatch === button;
      swatch.classList.toggle('is-active', isSelected);
      swatch.setAttribute('aria-pressed', String(isSelected));
    });

    if (button.dataset.productUrl) {
      card.querySelectorAll('a[href]').forEach((link) => {
        link.href = button.dataset.productUrl;
      });
    }
  }

  function bindEvents() {
    const variantSelects = getVariantSelects();
    bindVariantSelects(variantSelects);

    document.addEventListener('click', (event) => {
      const colorButton = event.target.closest?.('[data-joye-color-swatch]');
      if (colorButton) selectCardColor(colorButton);
    });

    // Collection, cart, and content pages only need product-card color swatches.
    // Skip product-variant listeners and sync timers when no product picker exists.
    if (!variantSelects) return;

    document.addEventListener('change', (event) => {
      if (!isVariantControl(event.target)) return;
      lastActivatedMediaId = '';
      scheduleJoyeMediaSync('document-change');
    });

    document.addEventListener('product-info:loaded', () => scheduleJoyeMediaSync('product-info-loaded'));
    document.addEventListener('variantChange', () => scheduleJoyeMediaSync('variant-change-event'));
    document.addEventListener('variant:change', () => scheduleJoyeMediaSync('variant-colon-change'));
    document.addEventListener('shopify:section:load', (event) => {
      bindVariantSelects(event.target?.querySelector?.('variant-selects') || getVariantSelects());
      lastActivatedMediaId = '';
      lastFilterSignature = '';
      scheduleJoyeMediaSync('section-load');
    });
    window.addEventListener('load', () => scheduleJoyeMediaSync('window-load'));

    if (getVariantIdFromUrl()) {
      scheduleJoyeMediaSync('url-variant');
      return;
    }

    scheduleJoyeMediaSync('initial');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindEvents);
  } else {
    bindEvents();
  }
})();

